/**
 * Christmas Delivery Cutoff Widget
 *
 * Displays Christmas delivery cutoff dates with countdown timer,
 * urgency messaging, and weight/region-aware cutoffs.
 * Auto-hides after December 25th.
 *
 * @class ChristmasDeliveryWidget
 * @extends {BaseWidget}
 */

(function() {
  'use strict';

  window.ProductWidgets = window.ProductWidgets || {};
  window.ProductWidgets.widgets = window.ProductWidgets.widgets || {};

  class ChristmasDeliveryWidget extends window.ProductWidgets.BaseWidget {
    constructor(orchestrator, container, productData) {
      super(orchestrator, container, productData);

      // ===========================================
      // CHRISTMAS CUTOFF DATES - UPDATE ANNUALLY
      // ===========================================
      // Last updated: December 2025 for Christmas 2025
      // Next update required: October/November 2026
      this.christmasYear = 2025;
      this.christmasDate = new Date(`${this.christmasYear}-12-25T23:59:59`);

      // Cutoff dates with 1pm deadline
      this.cutoffDates = {
        mainlandUK: new Date(`${this.christmasYear}-12-22T13:00:00`),   // December 22nd, 1pm - ALL mainland orders
        remoteAreas: new Date(`${this.christmasYear}-12-19T13:00:00`)   // December 19th - remote areas
      };

      // Remote postcode prefixes (2-day delivery areas)
      this.remotePostcodePrefixes = ['BT', 'AB', 'DD', 'KW', 'IV', 'KA', 'PH', 'HS', 'ZE', 'IM', 'PA'];

      // Widget state
      this.productWeight = 0;
      this.currentVariant = null;
      this.countdownInterval = null;
      this.modalId = `christmas-delivery-modal-${this.productData.id}`;

      // Configuration
      this.cutoffHour = 13; // 1pm

      this.initEventListeners();
    }

    initEventListeners() {
      document.addEventListener('variant:change', (event) => {
        if (event.detail && event.detail.variant) {
          this.handleVariantChange(event.detail.variant);
        }
      });

      this.on('product:variant-change', (data) => {
        if (data.variant) {
          this.handleVariantChange(data.variant);
        }
      });
    }

    /**
     * Check if widget should be hidden (after Christmas or all cutoffs passed)
     */
    shouldHideWidget() {
      const now = new Date();

      // Hide after Christmas
      if (now > this.christmasDate) {
        return true;
      }

      // Hide if the latest cutoff (Dec 22nd for mainland UK) has passed
      const latestCutoff = this.cutoffDates.mainlandUK;
      if (now > latestCutoff) {
        return true;
      }

      return false;
    }

    /**
     * Check if a postcode is in a remote area
     */
    isRemotePostcode(postcode) {
      if (!postcode) return false;
      const cleanPostcode = postcode.toUpperCase().trim().replace(/\s/g, '');

      return this.remotePostcodePrefixes.some(prefix =>
        cleanPostcode.startsWith(prefix)
      );
    }

    /**
     * Get the applicable cutoff based on product weight and region
     */
    getApplicableCutoff(postcode = null) {
      const isRemoteArea = this.isRemotePostcode(postcode);

      // Remote areas: December 19th
      if (isRemoteArea) {
        return {
          date: this.cutoffDates.remoteAreas,
          label: 'Remote Areas',
          description: 'Scottish Highlands, NI & Islands',
          weightClass: 'any'
        };
      }

      // Mainland UK: All orders - December 22nd
      return {
        date: this.cutoffDates.mainlandUK,
        label: 'Mainland UK',
        description: 'All orders',
        weightClass: 'all'
      };
    }

    /**
     * Calculate time remaining until cutoff
     */
    calculateTimeRemaining(cutoffDate) {
      const now = new Date();
      const diff = cutoffDate - now;

      if (diff <= 0) {
        return { passed: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      return { passed: false, days, hours, minutes, seconds };
    }

    /**
     * Get urgency level based on days remaining
     */
    getUrgencyLevel(daysRemaining) {
      if (daysRemaining <= 0) return 'passed';
      if (daysRemaining <= 3) return 'high';
      if (daysRemaining <= 7) return 'medium';
      return 'low';
    }

    /**
     * Get urgency message based on time remaining
     */
    getUrgencyMessage(timeRemaining, cutoffDate) {
      if (timeRemaining.passed) {
        return 'Christmas delivery no longer available';
      }

      if (timeRemaining.days === 0) {
        if (timeRemaining.hours === 0) {
          return `Last chance! ${timeRemaining.minutes} minutes left`;
        }
        return `Order today before 1pm!`;
      }

      if (timeRemaining.days === 1) {
        return 'Last day to order tomorrow!';
      }

      if (timeRemaining.days <= 3) {
        return 'Order now for Christmas delivery!';
      }

      if (timeRemaining.days <= 7) {
        return 'Order this week for Christmas delivery';
      }

      return 'Order soon for Christmas delivery';
    }

    /**
     * Format date for display (e.g., "Sat 21st Dec")
     */
    formatCutoffDate(date) {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      const day = date.getDate();
      const suffix = this.getOrdinalSuffix(day);

      return `${days[date.getDay()]} ${day}${suffix} ${months[date.getMonth()]}`;
    }

    /**
     * Get ordinal suffix for day (1st, 2nd, 3rd, etc.)
     */
    getOrdinalSuffix(day) {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    }

    /**
     * Find current variant
     */
    findCurrentVariant() {
      if (!this.productData.variants || this.productData.variants.length === 0) {
        return null;
      }

      if (this.productData.selectedVariantId) {
        const selected = this.productData.variants.find(
          v => v.id === this.productData.selectedVariantId
        );
        if (selected) return selected;
      }

      return this.productData.variants.find(v => v.available) || this.productData.variants[0];
    }

    /**
     * Handle variant change
     */
    handleVariantChange(variant) {
      const variantData = this.productData.variants.find(v => v.id === variant.id);
      if (!variantData) return;

      this.currentVariant = variantData;
      this.productWeight = this.currentVariant.weight ? (this.currentVariant.weight / 1000) : 0;

      this.render();
    }

    /**
     * Start the countdown timer
     */
    startCountdown() {
      // Clear any existing interval
      this.stopCountdown();

      // Update every minute
      this.countdownInterval = setInterval(() => {
        this.updateCountdownDisplay();
      }, 60000); // Update every minute
    }

    /**
     * Stop the countdown timer
     */
    stopCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    }

    /**
     * Update countdown display without full re-render
     */
    updateCountdownDisplay() {
      const cutoff = this.getApplicableCutoff();
      const timeRemaining = this.calculateTimeRemaining(cutoff.date);

      // Update countdown text
      const countdownEl = this.container.querySelector('.pw-christmas-countdown-value');
      if (countdownEl) {
        if (timeRemaining.passed) {
          countdownEl.textContent = 'Cutoff passed';
        } else {
          countdownEl.textContent = `${timeRemaining.days}d ${timeRemaining.hours}h ${timeRemaining.minutes}m`;
        }
      }

      // Check if we need to hide (all cutoffs passed)
      if (this.shouldHideWidget()) {
        this.container.style.display = 'none';
        this.stopCountdown();
      }
    }

    /**
     * Check if a specific cutoff has passed
     */
    hasCutoffPassed(cutoffDate) {
      return new Date() > cutoffDate;
    }

    /**
     * Main render method
     */
    render() {
      // Check if widget should be hidden
      if (this.shouldHideWidget()) {
        this.setContent('');
        return;
      }

      this.currentVariant = this.findCurrentVariant();
      if (!this.currentVariant) return;

      this.productWeight = this.currentVariant.weight ? (this.currentVariant.weight / 1000) : 0;

      const cutoff = this.getApplicableCutoff();
      const timeRemaining = this.calculateTimeRemaining(cutoff.date);
      const urgencyLevel = this.getUrgencyLevel(timeRemaining.days);

      const html = this.buildCardHTML(cutoff, timeRemaining, urgencyLevel) + this.buildModalHTML();
      this.setContent(html);

      this.attachEventListeners();
      this.startCountdown();
    }

    /**
     * Build the main card HTML - matches Assembly Info widget pattern
     */
    buildCardHTML(cutoff, timeRemaining, urgencyLevel) {
      const urgencyMessage = this.getUrgencyMessage(timeRemaining, cutoff.date);
      const formattedDate = this.formatCutoffDate(cutoff.date);

      // Dark theme colours (matching Assembly Info widget)
      const bgColor = 'rgb(var(--color-drawer-text))';
      const textColor = 'rgb(var(--color-drawer-background))';
      const mutedColor = 'rgb(var(--color-drawer-background) / 0.7)';
      const cardBg = 'rgb(var(--color-drawer-background) / 0.1)';
      const cardBorder = 'rgb(var(--color-drawer-background) / 0.2)';

      // Urgency colours
      const urgencyColors = {
        low: '34, 197, 94',      // Green
        medium: '251, 191, 36',   // Amber
        high: '239, 68, 68',      // Red
        passed: '107, 114, 128'   // Grey
      };
      const urgencyColor = urgencyColors[urgencyLevel] || urgencyColors.low;

      // Calculate progress bar percentage (days remaining out of ~14 day window)
      const maxDays = 14;
      const progressPercent = timeRemaining.passed ? 0 : Math.min(100, Math.round((timeRemaining.days / maxDays) * 100));

      return `
        <div class="pw-widget pw-christmas-delivery-widget" style="width: 100%;" data-button_hover="standard">
          <div
            class="pw-christmas-card"
            role="button"
            tabindex="0"
            aria-label="Click to view Christmas delivery cutoff dates"
            style="padding: 16px; background: ${bgColor}; border-radius: var(--rounded-card); cursor: pointer; transition: all 0.2s ease;">

            <!-- Header with Icon -->
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: ${cardBg}; border-radius: var(--rounded-button);">
                ${this.getGiftIconSVG('var(--color-drawer-background)')}
              </div>
              <div style="flex: 1;">
                <h3 class="pw-text-lg pw-font-bold" style="margin: 0; color: ${textColor};">Christmas Delivery</h3>
                <p class="pw-text-sm" style="margin: 0; color: ${mutedColor};">
                  ${timeRemaining.passed
                    ? 'Cutoff has passed'
                    : `Order by ${formattedDate} (1pm)`
                  }
                </p>
              </div>
            </div>

            ${!timeRemaining.passed ? `
              <!-- Countdown & Progress Section -->
              <div style="padding: 12px; margin-bottom: 12px; background: ${cardBg}; border-radius: var(--rounded-card); border: 1px solid ${cardBorder};">
                <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 10px;">
                  <div style="display: flex; align-items: center; gap: 8px;">
                    ${this.getClockIconSVG(textColor)}
                    <span class="pw-text-sm pw-font-bold pw-christmas-countdown-value" style="color: ${textColor};">
                      ${timeRemaining.days}d ${timeRemaining.hours}h ${timeRemaining.minutes}m
                    </span>
                  </div>
                  <span class="pw-text-sm" style="color: rgb(${urgencyColor}); font-weight: 600;">
                    ${urgencyLevel === 'high' ? '⚡ Urgent' : urgencyLevel === 'medium' ? '⏰ Order Soon' : '✓ Plenty of Time'}
                  </span>
                </div>

                <!-- Progress Bar -->
                <div style="width: 100%; height: 8px; background: rgba(255, 255, 255, 0.15); border-radius: 999px; overflow: hidden;">
                  <div style="width: ${100 - progressPercent}%; height: 100%; background: rgb(${urgencyColor}); border-radius: 999px; transition: width 0.3s ease;">&nbsp;</div>
                </div>
              </div>

              <!-- Urgency Message -->
              <div style="display: flex; align-items: start; gap: 8px; margin-bottom: 16px;">
                <svg style="width: 16px; height: 16px; flex-shrink: 0; margin-top: 2px; color: ${textColor};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span class="pw-text-sm" style="color: ${mutedColor};">
                  ${urgencyMessage}
                </span>
              </div>
            ` : `
              <!-- Passed Message -->
              <div style="padding: 12px; margin-bottom: 16px; background: ${cardBg}; border-radius: var(--rounded-card); border: 1px solid ${cardBorder};">
                <p class="pw-text-sm" style="margin: 0; color: ${mutedColor}; text-align: center;">
                  Christmas delivery cutoff has passed. Orders placed now will be delivered after Christmas.
                </p>
              </div>
            `}

            <!-- Learn More Button -->
            <div style="display: flex; justify-content: flex-end;">
              <button
                type="button"
                class="pw-learn-more"
                aria-controls="${this.modalId}"
                aria-expanded="false"
                style="color: ${mutedColor};"
              >
                <span>View all cutoff dates</span>
                <svg class="pw-learn-more__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>

          </div>
        </div>
      `;
    }

    /**
     * Build the modal HTML with delivery schedule
     */
    buildModalHTML() {
      const cutoffs = [
        {
          type: 'Mainland UK',
          description: 'All orders (any weight)',
          date: this.cutoffDates.mainlandUK,
          passed: this.hasCutoffPassed(this.cutoffDates.mainlandUK)
        },
        {
          type: 'Remote Areas',
          description: 'BT, AB, DD, KW, IV, KA, PH, HS, ZE, IM, PA',
          date: this.cutoffDates.remoteAreas,
          passed: this.hasCutoffPassed(this.cutoffDates.remoteAreas)
        }
      ];

      return `
        <x-modal id="${this.modalId}" class="x-modal drawer z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none" role="dialog" aria-modal="true" aria-label="Christmas Delivery Schedule" hidden>
          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" aria-controls="${this.modalId}" aria-expanded="false"></overlay-element>

          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">
            <gesture-element class="drawer__header flex items-center justify-between opacity-0 invisible relative">
              <span class="drawer__title heading font-bold title-md">Christmas Delivery Schedule</span>
              <button class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center" type="button" is="hover-button" aria-controls="${this.modalId}" aria-expanded="false" aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  <svg class="icon icon-close icon-sm" viewBox="0 0 20 20" stroke="currentColor" fill="none">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 15L15 5M5 5L15 15"></path>
                  </svg>
                </span>
              </button>
            </gesture-element>

            <div class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink">
              <div class="drawer__scrollable relative flex flex-col gap-5 md:gap-7 grow shrink">

                <!-- Hero Section -->
                <div class="text-center">
                  <div style="display: inline-flex; align-items: center; justify-content: center; width: 4rem; height: 4rem; background: rgb(var(--color-drawer-text)); border-radius: 50%; margin-bottom: 1rem;">
                    ${this.getGiftIconSVG('var(--color-drawer-background)')}
                  </div>
                  <h3 class="heading font-bold text-xl md:text-2xl" style="margin-bottom: 0.75rem; color: rgb(var(--color-drawer-text));">
                    Christmas Delivery Cutoffs
                  </h3>
                  <p class="text-base" style="max-width: 500px; margin: 0 auto; color: rgb(var(--color-drawer-text) / 0.7);">
                    Order before the cutoff time (<strong>1pm</strong>) on the dates below to receive your order in time for Christmas Day.
                  </p>
                </div>

                <!-- Cutoff Schedule Cards -->
                <div style="display: flex; flex-direction: column; gap: 12px;">
                  ${cutoffs.map(cutoff => `
                    <div style="padding: 16px; background: rgb(var(--color-drawer-text) / ${cutoff.passed ? '0.02' : '0.03'}); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / ${cutoff.passed ? '0.05' : '0.08'}); ${cutoff.passed ? 'opacity: 0.6;' : ''}">
                      <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;">
                        <div>
                          <div style="font-weight: 700; color: rgb(var(--color-drawer-text)); margin-bottom: 4px;">
                            ${cutoff.type}
                          </div>
                          <div style="font-size: 0.875rem; color: rgb(var(--color-drawer-text) / 0.6);">
                            ${cutoff.description}
                          </div>
                        </div>
                        <div style="text-align: right;">
                          <div style="font-weight: 700; color: rgb(var(--color-drawer-text));">
                            ${this.formatCutoffDate(cutoff.date)}
                          </div>
                          <div style="font-size: 0.75rem; color: rgb(var(--color-drawer-text) / 0.5);">
                            by 1pm
                          </div>
                        </div>
                      </div>
                    </div>
                  `).join('')}
                </div>

                <!-- Your Product Section -->
                <div style="padding: 1.5rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
                  <h4 class="font-bold text-base" style="margin-bottom: 1rem; color: rgb(var(--color-drawer-text));">Your Product</h4>
                  <div style="display: flex; flex-direction: column; gap: 12px;">
                    <div style="display: flex; align-items: center; gap: 10px;">
                      ${this.getScaleIconSVG()}
                      <span style="color: rgb(var(--color-drawer-text) / 0.85);">Weight: <strong>${this.productWeight.toFixed(1)}kg</strong></span>
                    </div>
                    <div style="display: flex; align-items: center; gap: 10px;">
                      ${this.getCalendarIconSVG()}
                      <span style="color: rgb(var(--color-drawer-text) / 0.85);">Your cutoff: <strong>${this.formatCutoffDate(this.getApplicableCutoff().date)}</strong> by 1pm</span>
                    </div>
                  </div>
                </div>

                <!-- Shop Pickup Option -->
                <div style="padding: 1.5rem; background: rgb(34, 197, 94, 0.08); border-radius: var(--rounded-card); border: 1px solid rgb(34, 197, 94, 0.2);">
                  <div style="display: flex; align-items: start; gap: 12px;">
                    ${this.getStoreIconSVG()}
                    <div style="flex: 1;">
                      <h4 class="font-bold text-base" style="margin: 0 0 0.5rem 0; color: rgb(var(--color-drawer-text));">Collect In-Store</h4>
                      <p class="text-sm" style="margin: 0 0 0.75rem 0; color: rgb(var(--color-drawer-text) / 0.85);">
                        Our shop is open until <strong>Christmas Eve (24th December) at 4pm</strong>. Click &amp; Collect is available for last-minute orders!
                      </p>
                      <address style="font-style: normal; font-size: 0.875rem; color: rgb(var(--color-drawer-text) / 0.7); line-height: 1.5;">
                        <strong style="color: rgb(var(--color-drawer-text));">RiiRoo Limited</strong><br>
                        5 Godwin Road<br>
                        Corby, Northamptonshire<br>
                        NN17 4DS
                      </address>
                    </div>
                  </div>
                </div>

                <!-- Postcode Checker -->
                <div style="padding: 1.5rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
                  <h4 class="font-bold text-base" style="margin-bottom: 0.5rem; color: rgb(var(--color-drawer-text));">Check Your Postcode</h4>
                  <p class="text-sm" style="margin-bottom: 1rem; color: rgb(var(--color-drawer-text) / 0.7);">Some areas have earlier cutoffs due to longer delivery times.</p>

                  <div style="display: flex; gap: 8px; margin-bottom: 12px;">
                    <input type="text"
                           id="christmas-postcode-input-${this.productData.id}"
                           placeholder="e.g., SW1A 1AA"
                           maxlength="8"
                           style="flex: 1; padding: 10px 14px; border: 1px solid rgb(var(--color-drawer-text) / 0.2); border-radius: var(--rounded-button); background: rgb(var(--color-drawer-background)); color: rgb(var(--color-drawer-text)); font-size: 1rem;">
                    <button type="button" class="pw-christmas-postcode-btn" style="padding: 10px 20px; background: rgb(var(--color-base-button)); color: rgb(var(--color-base-button-text)); border: none; border-radius: var(--rounded-button); font-weight: 600; cursor: pointer;">
                      Check
                    </button>
                  </div>
                  <div class="pw-christmas-postcode-result" style="display: none;"></div>
                </div>

                <!-- Important Notes -->
                <div style="padding: 1rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card);">
                  <div style="display: flex; align-items: start; gap: 0.75rem;">
                    ${this.getInfoIconSVG()}
                    <div style="flex: 1;">
                      <p class="text-sm" style="margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                        All orders must be placed before <strong>1pm</strong> on the cutoff date. Remote areas (Scottish Highlands, NI &amp; Islands) have an earlier cutoff. Pre-order items: cutoff applies to dispatch date, not order date.
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </x-modal>
      `;
    }

    /**
     * Attach event listeners
     */
    attachEventListeners() {
      // Postcode checker
      const postcodeBtn = this.container.querySelector('.pw-christmas-postcode-btn');
      if (postcodeBtn) {
        postcodeBtn.addEventListener('click', () => this.handlePostcodeCheck());
      }

      const postcodeInput = this.container.querySelector(`#christmas-postcode-input-${this.productData.id}`);
      if (postcodeInput) {
        postcodeInput.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') this.handlePostcodeCheck();
        });
      }

      // Make card clickable to open modal
      const card = this.container.querySelector('.pw-christmas-card');
      if (card) {
        card.addEventListener('click', (e) => {
          // Don't trigger if clicking the learn more button
          if (e.target.closest('.pw-learn-more')) {
            return;
          }
          const modal = document.getElementById(this.modalId);
          if (modal && !modal.open) {
            modal.show();
          }
        });

        // Keyboard event (Enter or Space)
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            card.click();
          }
        });

        // Hover effect - add subtle grey border
        card.addEventListener('mouseenter', () => {
          card.style.outline = '2px solid rgb(var(--color-drawer-background) / 0.4)';
          card.style.outlineOffset = '-2px';
        });

        card.addEventListener('mouseleave', () => {
          card.style.outline = 'none';
        });
      }
    }

    /**
     * Handle postcode check
     */
    handlePostcodeCheck() {
      const input = this.container.querySelector(`#christmas-postcode-input-${this.productData.id}`);
      const resultDiv = this.container.querySelector('.pw-christmas-postcode-result');

      if (!input || !resultDiv) return;

      const postcode = input.value.trim();

      if (!postcode) {
        resultDiv.innerHTML = `
          <div style="padding: 12px; background: rgb(239, 68, 68, 0.1); border-radius: var(--rounded-button); color: rgb(239, 68, 68); font-size: 0.875rem;">
            Please enter a postcode
          </div>
        `;
        resultDiv.style.display = 'block';
        return;
      }

      const isRemote = this.isRemotePostcode(postcode);
      const cutoff = this.getApplicableCutoff(postcode);
      const timeRemaining = this.calculateTimeRemaining(cutoff.date);

      if (isRemote) {
        resultDiv.innerHTML = `
          <div style="padding: 12px; background: rgb(245, 158, 11, 0.1); border-radius: var(--rounded-button);">
            <div style="display: flex; align-items: start; gap: 10px;">
              ${this.getWarningIconSVG()}
              <div>
                <div style="font-weight: 700; color: rgb(245, 158, 11); margin-bottom: 4px;">Remote Area Detected</div>
                <p style="margin: 0; color: rgb(var(--color-drawer-text) / 0.85); font-size: 0.875rem;">
                  Your postcode is in a remote delivery area with an earlier cutoff.
                </p>
                <p style="margin: 8px 0 0; color: rgb(var(--color-drawer-text)); font-size: 0.875rem;">
                  Order by: <strong>${this.formatCutoffDate(cutoff.date)}</strong> ${timeRemaining.passed ? '<span style="color: rgb(239, 68, 68);">(Passed)</span>' : ''}
                </p>
              </div>
            </div>
          </div>
        `;
      } else {
        resultDiv.innerHTML = `
          <div style="padding: 12px; background: rgb(34, 197, 94, 0.1); border-radius: var(--rounded-button);">
            <div style="display: flex; align-items: start; gap: 10px;">
              ${this.getCheckCircleIconSVG()}
              <div>
                <div style="font-weight: 700; color: rgb(34, 197, 94); margin-bottom: 4px;">Mainland UK</div>
                <p style="margin: 0; color: rgb(var(--color-drawer-text) / 0.85); font-size: 0.875rem;">
                  Standard delivery available.
                </p>
                <p style="margin: 8px 0 0; color: rgb(var(--color-drawer-text)); font-size: 0.875rem;">
                  Order by: <strong>${this.formatCutoffDate(cutoff.date)}</strong> ${timeRemaining.passed ? '<span style="color: rgb(239, 68, 68);">(Passed)</span>' : ''}
                </p>
              </div>
            </div>
          </div>
        `;
      }

      resultDiv.style.display = 'block';
    }

    /**
     * Clean up on destroy
     */
    destroy() {
      this.stopCountdown();

      const modal = document.getElementById(this.modalId);
      if (modal) modal.remove();

      super.destroy();
    }

    // ===========================================
    // SVG ICONS
    // ===========================================

    getGiftIconSVG(colourVar = 'var(--color-drawer-background)') {
      return `<svg style="width: 20px; height: 20px; flex-shrink: 0; color: rgb(${colourVar});" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"></path></svg>`;
    }

    getCalendarIconSVG() {
      return `<svg style="width: 18px; height: 18px; flex-shrink: 0; color: rgb(var(--color-drawer-text) / 0.6);" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>`;
    }

    getScaleIconSVG() {
      return `<svg style="width: 18px; height: 18px; flex-shrink: 0; color: rgb(var(--color-drawer-text) / 0.6);" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"></path></svg>`;
    }

    getClockIconSVG(colorVar = 'rgb(var(--color-drawer-background))') {
      return `<svg style="width: 16px; height: 16px; flex-shrink: 0; color: ${colorVar};" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>`;
    }

    getCheckCircleIconSVG() {
      return `<svg style="width: 20px; height: 20px; flex-shrink: 0; color: rgb(34, 197, 94);" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>`;
    }

    getWarningIconSVG() {
      return `<svg style="width: 20px; height: 20px; flex-shrink: 0; color: rgb(245, 158, 11);" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path></svg>`;
    }

    getInfoIconSVG() {
      return `<svg style="width: 20px; height: 20px; flex-shrink: 0; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>`;
    }

    getStoreIconSVG() {
      return `<svg style="width: 24px; height: 24px; flex-shrink: 0; color: rgb(34, 197, 94);" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>`;
    }
  }

  window.ProductWidgets.widgets.ChristmasDeliveryWidget = ChristmasDeliveryWidget;

})();
